import { useEffect, useState } from "react";
import { socket } from "../../socket";

export const DOCK_CHANGED_EVENT = "dock-operations-changed";
/**
 * VMS pushes on every checkpoint change, so this poll is only a safety net: for camera health and
 * snapshots, which Devices writes and VMS cannot emit on, and for dock trips advanced from elsewhere
 * in Manage Trip. It can be relaxed once Devices emits on snapshot write and heartbeat transitions.
 */
export const DOCK_FALLBACK_POLL_MS = 30000;
export const DOCK_REFETCH_DEBOUNCE_MS = 400;

/**
 * Refetches when VMS says something changed, rather than polling to discover it.
 * A truck arriving fires several camera events within a second or two, so the refetch is debounced —
 * otherwise "refetch once per change" becomes four requests. A socket reconnect refetches too, because
 * anything VMS emitted while this client was disconnected was never delivered.
 *
 * Shared by the board and the operations table. Both subscribing is correct and not wasteful: the two
 * panels read different endpoints, and each debounces its own refetch.
 */
export function useDockChangeRefetch(plantId, refetch) {
	useEffect(() => {
		if (!plantId) return undefined;
		let timer = null;
		const scheduleRefetch = () => {
			clearTimeout(timer);
			timer = setTimeout(() => refetch(), DOCK_REFETCH_DEBOUNCE_MS);
		};
		// One socket can be joined to several plant rooms, so another plant's event must not refetch this board.
		const onDockChanged = (message) => {
			if (String(message?.plantId || "") !== String(plantId)) return;
			scheduleRefetch();
		};
		socket.on(DOCK_CHANGED_EVENT, onDockChanged);
		socket.on("connect", scheduleRefetch);
		return () => {
			clearTimeout(timer);
			socket.off(DOCK_CHANGED_EVENT, onDockChanged);
			socket.off("connect", scheduleRefetch);
		};
	}, [plantId, refetch]);
}

// Pauses the fallback poll while the tab is hidden, so idle boards cost the plant nothing.
export function useTabVisible() {
	const [visible, setVisible] = useState(() => !document.hidden);
	useEffect(() => {
		const onVisibilityChange = () => setVisible(!document.hidden);
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => document.removeEventListener("visibilitychange", onVisibilityChange);
	}, []);
	return visible;
}

/**
 * Re-renders once a second while `enabled`, so running durations advance without refetching.
 *
 * Gated rather than always on: a table showing only finished operations has nothing that moves, and
 * must not re-render every second to redraw identical numbers.
 */
export function useSecondsTick(enabled) {
	const [, forceTick] = useState(0);
	useEffect(() => {
		if (!enabled) return undefined;
		const interval = setInterval(() => forceTick((tick) => tick + 1), 1000);
		return () => clearInterval(interval);
	}, [enabled]);
}
